import { ChevronLeftIconRounded } from '@shared/assets/icons';
import { IconButton } from '@shared/ui/icon-button';
import { FC, useCallback, useMemo, useState } from 'react';
import { calcSmsParts20_70 } from '@shared/libs/sms-utils';
import { getTextFromHtml } from '@features/modal-invitation/lib/getTextFromHtml';
import { useSendMessagesFlow } from '@pages/event-send-invitations/lib/use-send-messages-flow';
import { SendProgressLoader } from '@pages/event-send-invitations/ui/send-progress-loader';
import { SendStatisticsModal } from '@pages/event-send-invitations/ui/send-statistics-modal';
import { buildTicketDeliveryPreviewChannels } from '@pages/event-send-invitations/lib/sms-cost-preview';
import {
  INVITATION_TYPE_EMAIL,
  INVITATION_TYPE_SMS,
  INVITATION_TYPE_VK_TEAMS,
} from '@pages/event-send-invitations/lib/invitation-types';
import { SendContentType, type SendMessagesInput } from '@pages/event/api/useSendMessages';
import type { SelectValue } from '@shared/ui/select';
import { TicketSendSettingsSection } from './ui/ticket-send-settings-section';
import { TicketPreviewSummarySection } from './ui/preview-summary-section';
import { ticketSendPayload } from './lib/ticket-send-payload';
import styles from './event-send-tickets-page.module.scss';
import { EventStartedSend } from '@entities/event';

const TICKET_TYPE_REQUIRED = 'Обязательное поле';
const TICKET_TEXT_REQUIRED = 'Введите текст билета';

const cleanSingleLineText = (value: string) => value.replace(/\r?\n/g, '');

export interface EventSendTicketsScreenProps {
  onBack: () => void;
  eventId: number;
  eventName: string | null;
  eventDateStart: string | null;
  ticketIds: number[];
  emailWillBeSent: number;
  smsWillBeSent: number;
  vkWillBeSent: number;
  sendMessages: (input: SendMessagesInput) => Promise<number | undefined>;
  onSent?: () => void;
}

export const EventSendTicketsScreen: FC<EventSendTicketsScreenProps> = ({
  onBack,
  eventId,
  eventName,
  eventDateStart,
  ticketIds,
  emailWillBeSent,
  smsWillBeSent,
  vkWillBeSent,
  sendMessages,
  onSent,
}) => {
  const {
    runSend,
    closeStatistics,
    isWaitingForSend,
    isStatisticsModalVisible,
    isSendFlowActive,
    isStartedFlowActive,
    messagesStats,
  } = useSendMessagesFlow({ eventId, contentType: SendContentType.Ticket, onComplete: onSent });

  const [selectedTicketChannels, setSelectedTicketChannels] = useState<SelectValue[]>([]);
  const [ticketText, setTicketText] = useState('');
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketTypeError, setTicketTypeError] = useState<string | null>(null);
  const [ticketTextError, setTicketTextError] = useState<string | null>(null);

  const ticketTextPlain = useMemo(() => getTextFromHtml(ticketText).trim(), [ticketText]);
  const isSmsTicketMode =
    selectedTicketChannels.length === 0 || !selectedTicketChannels.includes(INVITATION_TYPE_EMAIL);
  const isEmailTicketMode = selectedTicketChannels.includes(INVITATION_TYPE_EMAIL);
  const isBothTicketMode =
    selectedTicketChannels.includes(INVITATION_TYPE_EMAIL) &&
    (selectedTicketChannels.includes(INVITATION_TYPE_SMS) || selectedTicketChannels.includes(INVITATION_TYPE_VK_TEAMS));
  const ticketSmsText = isSmsTicketMode ? ticketText : ticketTextPlain;
  const ticketTextParts = useMemo(() => calcSmsParts20_70(ticketSmsText), [ticketSmsText]);

  const previewLink = globalThis.window?.location?.href || '';

  const eventDateLabel = useMemo(() => {
    if (!eventDateStart) return '';
    const date = new Date(eventDateStart);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      weekday: 'long',
    });
  }, [eventDateStart]);

  const ticketChannels = useMemo(
    () =>
      buildTicketDeliveryPreviewChannels(
        selectedTicketChannels,
        emailWillBeSent,
        smsWillBeSent,
        vkWillBeSent,
        false,
        ticketTextParts
      ),
    [selectedTicketChannels, emailWillBeSent, smsWillBeSent, vkWillBeSent, ticketTextParts]
  );

  const handleTicketChannelsChange = useCallback((values: SelectValue | SelectValue[]) => {
    const channels = Array.isArray(values) ? values : [];
    setSelectedTicketChannels(channels);
    setTicketTypeError(null);
    if (!channels.includes(INVITATION_TYPE_EMAIL)) {
      setTicketText((prevText) => cleanSingleLineText(getTextFromHtml(prevText)));
    }
  }, []);

  const handleTicketTextChange = useCallback((value: string) => {
    setTicketText(cleanSingleLineText(value));
    setTicketTextError(null);
  }, []);

  const validateOnSubmit = useCallback(() => {
    const typeIssue = selectedTicketChannels.length ? null : TICKET_TYPE_REQUIRED;
    const textIssue = ticketTextPlain ? null : TICKET_TEXT_REQUIRED;

    setTicketTypeError(typeIssue);
    setTicketTextError(textIssue);

    return !(typeIssue || textIssue);
  }, [selectedTicketChannels, ticketTextPlain]);

  const handleSend = useCallback(() => {
    if (!validateOnSubmit()) {
      return;
    }

    void runSend(async () => {
      const payload = ticketSendPayload({
        ticketIds,
        selectedChannels: selectedTicketChannels,
        ticketText,
        ticketSubject,
      });
      return sendMessages(payload);
    });
  }, [validateOnSubmit, runSend, ticketIds, selectedTicketChannels, ticketText, ticketSubject, sendMessages]);

  const handleBack = useCallback(() => {
    if (isSendFlowActive) return;
    onBack();
  }, [isSendFlowActive, onBack]);

  return (
    <main className={styles.container}>
      {!isStartedFlowActive && (
        <nav className={styles.back}>
          <IconButton
            onClick={handleBack}
            icon={<ChevronLeftIconRounded variant="onDeep" />}
            variant="primary"
            size="small"
          >
            Назад
          </IconButton>
        </nav>
      )}

      {!isSendFlowActive && !isStartedFlowActive && (
        <div className={styles.content}>
          <div className={styles.settingsColumn}>
            <section className={styles.settingsPanel}>
              <TicketSendSettingsSection
                values={{
                  selectedTicketChannels,
                  isSmsTicketMode,
                  isBothTicketMode,
                  isEmailTicketMode,
                  ticketSmsText,
                  ticketText,
                  ticketSubject,
                  ticketTextParts,
                  previewLink,
                }}
                validation={{
                  ticketTypeError,
                  ticketTextError,
                }}
                actions={{
                  onTicketChannelsChange: handleTicketChannelsChange,
                  onTicketSubjectChange: setTicketSubject,
                  onTicketTextChange: handleTicketTextChange,
                }}
              />
            </section>
          </div>
          <TicketPreviewSummarySection
            onSendClick={handleSend}
            eventName={eventName}
            eventDateLabel={eventDateLabel}
            ticketsCount={ticketIds.length}
            channels={ticketChannels}
          />
        </div>
      )}

      {isStartedFlowActive && <EventStartedSend title="Начали отправку билетов" onClose={closeStatistics} />}
      {isWaitingForSend && <SendProgressLoader title="Уже отправляем билеты" />}
      <SendStatisticsModal
        active={isStatisticsModalVisible}
        messagesStats={messagesStats}
        contentType={SendContentType.Ticket}
        onClose={closeStatistics}
      />
    </main>
  );
};
